import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Download, ShieldCheck, FileText, ChevronLeft } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { productKey } = useParams();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/storefront/products/${productKey}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [productKey]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">Product not available. <Link to="/shop" className="text-[#3B1F6E] font-semibold">Back to shop</Link></div>;
  if (!p) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-400">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#3B1F6E] mb-8"><ChevronLeft className="w-4 h-4" /> Back to shop</Link>
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="relative aspect-[3/4] max-w-md rounded-2xl overflow-hidden bg-gradient-to-br from-[#3B1F6E] to-[#0A0F1D] shadow-xl">
          {p.cover_url ? <img src={assetUrl(p.cover_url)} alt={p.title} className="w-full h-full object-cover" /> :
            <div className="w-full h-full flex items-center justify-center"><FileText className="w-16 h-16 text-white/30" /></div>}
        </div>
        <div>
          <span className="inline-block bg-[#D97706] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-4">{p.category}</span>
          <h1 data-testid="product-title" className="font-serif-display text-4xl sm:text-5xl font-bold text-[#0A0F1D] tracking-tight leading-tight">{p.title}</h1>
          {p.subtitle && <p className="text-lg text-slate-500 mt-3">{p.subtitle}</p>}
          <div className="flex items-center gap-1.5 mt-5 flex-wrap">
            {p.formats?.map((f) => <span key={f} className="text-xs font-semibold text-[#3B1F6E] bg-[#3B1F6E]/8 px-3 py-1 rounded-full">{f}</span>)}
          </div>
          <p className="mt-6 text-slate-600 leading-relaxed whitespace-pre-line">{p.description}</p>
          <div className="mt-8 flex items-center gap-5">
            <span data-testid="product-price" className="font-ui text-3xl font-bold text-[#2E1065]">${p.price.toFixed(2)}</span>
            <button data-testid="product-add-to-cart" onClick={() => { add(p); toast.success("Added to cart"); }}
              className="flex items-center gap-2 bg-[#3B1F6E] hover:bg-[#2E1065] text-white font-semibold px-8 py-3.5 rounded-full transition-colors">
              <Plus className="w-4 h-4" /> Add to Cart
            </button>
          </div>
          <div className="mt-8 grid gap-3 border-t pt-6">
            <div className="flex items-center gap-2.5 text-sm text-slate-600"><Download className="w-4 h-4 text-[#D97706]" /> {p.file_count} file(s) · instant download after purchase</div>
            <div className="flex items-center gap-2.5 text-sm text-slate-600"><ShieldCheck className="w-4 h-4 text-[#D97706]" /> Secure checkout · yours to keep forever</div>
          </div>
        </div>
      </div>
    </div>
  );
}
